import { Injectable, Injector } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorDialogComponent } from '@Work/Accounts/proprietary/dialogs/error-dialog/error-dialog.component';
import { Datasource } from './datasource';

export interface G2AppError {
    errorId: string;
    type: string;
    errorMessage: string;
    source?: any | null;
    stack?: string;
}

export interface G2ApiError {
    errorId: string;
    type: string;
    errorMessage: string;
    errorsToDisplay: string[];
    source?: any | null;
    objectType?: string;
    tranType?: string;
    httpStatusCode?: number | null;
    apiReplyCode?: number | null;
    apiReplyMessage?: string;
}

export interface G2Error {
    errorId: string;
    type: string;
    errorMessage: string;
    errorsToDisplay: string[];
    errorsToCopy: string;
    isAllowedToCopy: boolean;
    source?: any | null;
    stack?: string;
    objectType?: string;
    tranType?: string;
    httpStatusCode?: number | null;
    apiReplyCode?: number | null;
    apiReplyMessage?: string;
    badgeText?: string;
}

@Injectable({ providedIn: 'root' })
export class ErrorService {
    _errorIdCounter: number = 0;

    dsExtractedMessages = [];

    isDebugMode: boolean = false;

    constructor(private modalService: NgbModal, public injector: Injector) { }

    public showError(errorValue: any, source?: Datasource | any) {
        let displayErrors: string[] = [];

        if (errorValue.message && typeof errorValue.message === 'string') {
            displayErrors.push(errorValue.message);
        } else if (source && typeof source == 'object') {
            displayErrors = this.extractProcessMessages(source);
        } else {
            displayErrors.push('App Error. Error could not be decoded');
        }

        let error: G2Error = {
            errorId: this.setErrorIdCounter(),
            type: errorValue.type,
            errorMessage: errorValue.message,
            source: source ? source : null,
            stack: errorValue.stack ? errorValue.stack : '',
            objectType: source ? source._objectType : '',
            tranType: source ? source._tranType : '',
            httpStatusCode: source ? source._httpResponseCode : null,
            apiReplyCode: source ? source._replyCode : null,
            apiReplyMessage: source ? source._replyMessage : '',
            errorsToDisplay: displayErrors,
            isAllowedToCopy: this.isDebugMode,
            errorsToCopy: this.formatErrorsForCopy(displayErrors),
            badgeText: this.getBadgeText(errorValue.type)
        };

        this.openErrorDialog(error);
    }

    public openErrorDialog(error: G2Error) {
        const modalRef = this.modalService.open(ErrorDialogComponent, {
            centered: true,
            size: 'lg',
            backdrop: 'static',
            windowClass: 'ep-modal-edit-panel'
        });

        modalRef.componentInstance.error = error;

        modalRef.result.then(() => { });
    }

    public setIsClientDebugMode(debugMode: boolean) {
        this.isDebugMode = debugMode;
    }

    private setErrorIdCounter(): string {
        return (this._errorIdCounter++).toString();
    }

    private extractProcessMessages(source: Datasource | any) {
        let data;
        let messages = [];

        if (!source) {
            return messages;
        }

        if (source instanceof Datasource) {
            data = source.data?.ConceptOneReply;
        } else {
            data = source.data ? source.data : source;
        }

        this.dsExtractedMessages = [];

        if (typeof data === 'object') {
            this.searchForErrors(data);
            messages = [...this.dsExtractedMessages];
        } else {
            messages.push(data);
        }

        return messages;
    }

    private searchForErrors(item) {
        Object.keys(item).forEach((key) => {
            if (typeof item[key] === 'object') {
                this.searchForErrors(item[key]);
            }
            if (typeof item[key] === 'string' && key === 'Process_Message' && item['Process_Status'] === 'ERR') {
                this.dsExtractedMessages.push(item[key]);
            }
        });
    }

    private extractErrorMessage(errorData) {
        return errorData.message ? errorData.message : '';
    }

    private formatErrorsForCopy(errorMessages: string | string[]) {
        if (!Array.isArray(errorMessages)) {
            return errorMessages;
        }

        let errorText = errorMessages.join(' + ');
        let formattedErrorForCopy = errorText.replace(/\+/g, '');
        return formattedErrorForCopy;
    }

    private getBadgeText(type: string) {
        if (type == 'app') {
            return 'App';
        } else {
            return 'Page';
        }
    }
}
